// src/components/ExploreAgents.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebase";
import {
  collectionGroup,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  setDoc,
  updateDoc,
  increment,
  doc,
} from "firebase/firestore";
import { useAuth, useAuthRedirect } from "../context/AuthContext";
import SessionSidebar from "../components/SessionSidebar";
import { useUserPlan } from "../context/UserPlanContext";
import CustomAlert from "../components/CustomAlert";

export default function ExploreAgents() {
  const [publicAgents, setPublicAgents] = useState<any[]>([]);
  const [tagFilter, setTagFilter] = useState("");
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<
    { id: number; message: string; type: "success" | "error" | "warning" }[]
  >([]);
  const [alertId, setAlertId] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { isPro, loading: planLoading } = useUserPlan();
  const { user, loading: authLoading } = useAuth();
  useAuthRedirect();

  const showAlert = (
    message: string,
    type: "success" | "error" | "warning" = "error"
  ) => {
    const newAlert = { id: alertId, message, type };
    setAlerts((prev) => [...prev, newAlert]);
    setAlertId((prev) => prev + 1);
  };

  const closeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  // ─── 1) Real‐time listener for all agents where isPublic == true, ordered by favoriteCount desc ──────────────────────────────────
  useEffect(() => {
    if (authLoading || planLoading || !user) return;

    const q = query(
      collectionGroup(db, "agents"),
      where("isPublic", "==", true),
      orderBy("favoriteCount", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const agents = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as any),
        }));
        setPublicAgents(agents);
      },
      (error) => {
        console.error("Failed to fetch public agents:", error);
        setPublicAgents([]);
      }
    );
    return () => unsub();
  }, [authLoading, planLoading, user]);

  // ─── 2) “Use this agent” clones it into the user’s own agents list ─────────────────────────────────────────────────────────
  const handleUseAgent = async (agent: any) => {
    if (authLoading || !user) return;
    const userId = user.uid;
    const newId =
      agent.name.toLowerCase().replace(/\s+/g, "-") + "-" + Date.now();
    const newAgent = {
      ...agent,
      createdAt: serverTimestamp(),
      creatorId: agent.creatorId || "anonymous",
      isPublic: false,
      favoriteCount: 0, // Reset favorites on clone
    };

    try {
      await setDoc(doc(db, `users/${userId}/agents/${newId}`), newAgent);
      showAlert("Agent saved to your account!");
      navigate("/library");
    } catch (err) {
      console.error("Failed to save agent to user library:", err);
      showAlert("Could not save this agent to your library. Please try again.");
    }
  };

  // ─── 3) “Favorite” increments both user’s favorites and the agent’s global favoriteCount ─────────────────────────────────────────────────────────
  const handleFavorite = async (agent: any) => {
    if (authLoading || !user) return;
    const userId = user.uid;

    const agentRef = doc(db, `favorites/${userId}/agents/${agent.id}`);
    const agentGlobalRef = doc(
      db,
      `users/${agent.creatorId}/agents/${agent.id}`
    );

    try {
      await setDoc(agentRef, { favoritedAt: serverTimestamp() });
      await updateDoc(agentGlobalRef, {
        favoriteCount: increment(1),
      });
      showAlert("Favorited!");
    } catch (error) {
      console.error("Failed to favorite agent:", error);
      showAlert("Failed to favorite agent. Please try again.");
    }
  };

  // ─── 4) Filter by tag if tagFilter is non‐empty ─────────────────────────────────────────────────────────
  const filteredAgents = publicAgents.filter((a) =>
    tagFilter ? a.tags?.includes(tagFilter) : true
  );

  if (authLoading || planLoading) {
    return <div className="p-6 text-offwhite">Loading...</div>;
  }
  if (!user) {
    return null;
  }

  const userId = user.uid;

  return (
    <div className="flex h-screen text-sm ">
      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          message={alert.message}
          type={alert.type}
          onClose={() => closeAlert(alert.id)}
        />
      ))}
      <header className="fixed top-0 left-0 w-full bg-secondary border-b border-darkgray p-4 flex justify-between items-center z-50 md:hidden">
        <button
          aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
          aria-expanded={isSidebarOpen}
          className="p-2 bg-teal text-offwhite rounded-lg hover:bg-teal/80 transition"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          {isSidebarOpen ? "✖" : "☰"}
        </button>
        <h2 className="text-lg font-semibold text-offwhite">My Agents</h2>
      </header>
      <div
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-secondary border-r border-darkgray p-6 transform ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:relative md:translate-x-0 md:flex md:h-full transition-transform duration-300 ease-in-out`}
      >
        <SessionSidebar userId={userId} isPro={isPro} />
      </div>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="flex-1 p-6 pt-20 md:pt-14 bg-charcoal  overflow-y-auto">
        <h1 className="text-2xl font-bold text-offwhite mb-4">
          Discover Public Agents
        </h1>
        <input
          type="text"
          placeholder="Search by tag (e.g. support)"
          className="w-full p-3 mb-4 bg-secondary border border-darkgray rounded-lg text-offwhite focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value.toLowerCase())}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 bg-charcoal">
          {filteredAgents.map((agent) => (
            <div
              key={agent.id}
              className="p-4 bg-secondary border border-darkgray rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <h2 className="text-lg font-semibold text-offwhite">
                {agent.name}
              </h2>
              <p className="text-sm text-gray-400 mb-2">Model: {agent.model}</p>
              <p className="text-sm italic text-offwhite mb-2">
                {agent.prompt.slice(0, 160)}...
              </p>
              <div className="flex flex-wrap gap-1 mt-2">
                {agent.tags?.map((tag: string) => (
                  <span
                    key={tag}
                    className="text-xs bg-teal/20 text-offwhite px-2 py-1 rounded"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                ❤️ {agent.favoriteCount || 0} favorites
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => handleUseAgent(agent)}
                  className="px-3 py-1 bg-teal text-offwhite rounded-lg text-sm hover:bg-teal/80 transition"
                >
                  ➕ Use This Agent
                </button>
                <button
                  onClick={() => handleFavorite(agent)}
                  className="px-3 py-1 bg-yellow-600 text-offwhite rounded-lg text-sm hover:bg-yellow-700 transition"
                >
                  ⭐ Favorite
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
